import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, RotateCcw, Trash2, ShieldCheck, Loader2, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function NewRoleForm({ roles, onCreated }) {
  const [label, setLabel] = useState("");
  const [template, setTemplate] = useState("haustechnik");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!label.trim()) return toast.error("Bitte einen Namen eingeben");
    setBusy(true);
    try {
      const { data } = await api.post("/roles", { label, template });
      toast.success(`Rolle „${data.label}“ angelegt`); setLabel(""); onCreated(data.key);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-dashed border-slate-300 rounded-xl p-3 space-y-2" data-testid="new-role-form">
      <div className="text-xs font-semibold text-slate-600">Neue Rolle</div>
      <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z. B. Reinigungsdienst" className="h-9" data-testid="new-role-name" />
      <Select value={template} onValueChange={setTemplate}>
        <SelectTrigger className="h-9 text-sm" data-testid="new-role-template"><SelectValue /></SelectTrigger>
        <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>Vorlage: {r.label}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" className="w-full" onClick={create} disabled={busy} data-testid="new-role-create"><Plus className="h-4 w-4 mr-1" />Rolle anlegen</Button>
    </div>
  );
}

function PermEditor({ role, permissions, onSaved, onDeleted }) {
  const [sel, setSel] = useState(role.perms);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(role.perms); }, [role]);
  const groups = useMemo(() => permissions.reduce((a, p) => ({ ...a, [p.group]: [...(a[p.group] || []), p] }), {}), [permissions]);
  const dirty = [...sel].sort().join() !== [...role.perms].sort().join();
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={`perm-editor-${role.key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-head font-bold text-slate-900">{role.label}</div>
          <div className="text-xs text-slate-500">{role.builtin ? "Standardrolle" : "Eigene Rolle"} · {role.user_count} Benutzer</div>
        </div>
        <div className="flex gap-2">
          {role.builtin && !role.locked && <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.post(`/roles/${role.key}/reset`), "Standardrechte wiederhergestellt")} data-testid="role-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" />Standard</Button>}
          {!role.builtin && <Button size="sm" variant="outline" className="text-red-600" disabled={busy || role.user_count > 0} title={role.user_count ? "Erst Benutzer einer anderen Rolle zuweisen" : ""}
            onClick={() => { setBusy(true); api.delete(`/roles/${role.key}`).then(() => { toast.success("Rolle gelöscht"); onDeleted(); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setBusy(false)); }} data-testid="role-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" />Löschen</Button>}
        </div>
      </div>
      {role.locked && <div className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-50 rounded-lg p-2"><Lock className="h-3.5 w-3.5" />Administratoren haben immer alle Rechte und verwalten Benutzer & Rollen.</div>}
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
        {Object.entries(groups).map(([g, items]) => (
          <div key={g}>
            <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{g}</div>
            {items.map((p) => { const fixed = role.locked || (p.key === "defect:approve" && !["leitung", "geschaeftsleitung"].includes(role.key)); return (
              <label key={p.key} className={`flex items-center gap-2.5 px-3 py-2 text-sm ${fixed ? "opacity-70" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`perm-${role.key}-${p.key}`}>
                <Checkbox checked={sel.includes(p.key)} disabled={fixed} onCheckedChange={() => toggle(p.key)} />
                <span className="text-slate-700">{p.label}{p.key === "defect:approve" && <span className="block text-[11px] text-slate-400">Nur Administrator, Geschäftsleitung und Leitung</span>}</span>
              </label>
            ); })}
          </div>
        ))}
      </div>
      {!role.locked && <Button className="w-full" disabled={!dirty || busy} onClick={() => run(() => api.put(`/roles/${role.key}`, { perms: sel }), "Rechte gespeichert")} data-testid="role-save-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rechte speichern"}</Button>}
      <p className="text-[11px] text-slate-400">Rechte gelten zusätzlich nur für die Projekte, die einem Benutzer zugewiesen sind. Änderungen wirken innerhalb weniger Sekunden.</p>
    </div>
  );
}

export default function RolesManager({ open, onOpenChange, onRolesChanged }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("haustechnik");
  const load = () => api.get("/roles").then((r) => { setData(r.data); onRolesChanged && onRolesChanged(r.data.roles); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const role = data?.roles.find((r) => r.key === active) || data?.roles[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="roles-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" />Rollen & Rechte</DialogTitle>
          <DialogDescription>Rollen anlegen und festlegen, was jede Rolle darf.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="h-40 bg-slate-100 rounded-xl animate-pulse" /> : (
          <div className="grid sm:grid-cols-[200px_1fr] gap-4">
            <div className="space-y-3">
              <div className="flex sm:flex-col gap-1.5 flex-wrap">
                {data.roles.map((r) => (
                  <button key={r.key} onClick={() => setActive(r.key)} data-testid={`role-tab-${r.key}`}
                    className={`text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${role?.key === r.key ? "bg-primary text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    {r.label}{!r.builtin && <span className="ml-1 text-[10px] opacity-70">(eigene)</span>}
                  </button>
                ))}
              </div>
              <NewRoleForm roles={data.roles} onCreated={(k) => { setActive(k); load(); }} />
            </div>
            {role && <PermEditor role={role} permissions={data.permissions} onSaved={load} onDeleted={() => { setActive("haustechnik"); load(); }} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
